import BotoesSalvarExcluir from '@/shared/components/forms/BotoesSalvarExcluir';
import Carregando from '@/shared/components/Carregando';
import Links from '@/shared/constants/navegacao';
import ModalExcluir from '@/shared/components/modals/ModalExcluir';
import ModalHistorico from '@/modules/system/historico/ModalHistorico';
import Select from 'react-select';
import { Controller, SubmitHandler, useForm, type Resolver } from 'react-hook-form';
import { CreateDistritoDto, ReadDefensoriaDto, ReadDistritoDto } from '@/api.types';
import { distritoFormSchema } from '@/modules/structure/distrito/distrito.schema';
import { EditadoPorRodape } from '@/shared/components/EditadoPorRodape';
import { FieldError, formControlClass } from '@/shared/components/forms/FormFieldFeedback';
import { numberInputValueAs } from '@/shared/utils/formZod.utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { GetDefensorias } from '@/modules/structure/defensoria/defensoria.service';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { useNavigate } from 'react-router-dom';
import { Tabelas } from '@/shared/constants/tables';
import { createDistrito, deleteDistrito, readDistrito, updateDistrito } from './distrito.service';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

export default function DistritoEditPage() {
  const { control, getValues, handleSubmit, register, reset, formState: { errors } } = useForm<ReadDistritoDto>({
    resolver: zodResolver(distritoFormSchema) as unknown as Resolver<ReadDistritoDto>,
    mode: 'onTouched',
    defaultValues: { ordem: 0, nome: '' },
  });
  const { setActions } = useBreadcrumbActions();
  const navigate = useNavigate();
  const params = useParams();

  const [isExecuting, setIsExecuting] = useState(false);
  const [listsLoaded, setListLoaded] = useState(false);
  const [modaExcluirShow, setModalExcluirShow] = useState(false);
  const [modalHistorico, setModalHistorico] = useState(false);

  const id = params.id ? Number(params.id) : 0;
  const btnSalvar = id > 0 ? 'Salvar' : 'Cadastrar';

  const [defensorias, setDefensorias] = useState<ReadDefensoriaDto[]>([]);

  useEffect(() => {
    setActions({
      secondary: { label: 'Novo', href: Links.Distrito_Criar, onClick: () => {} },
      history: { label: 'Histórico', onClick: () => setModalHistorico(true) },
    });
    return () => setActions({});
  }, [setActions]);

  // -- READ -- //
  useEffect(() => {
    Promise.all([
      GetDefensorias(0, 1000),
      id > 0 ? readDistrito({ skip: 0, take: 1, id: id, comDefensoria: true }) : null
    ]).then((results) => {
      setDefensorias(results[0].objects);

      const distrito = results[1]?.[0].shift()
      reset(distrito);
    }).catch((error) => {
      toast.error(String(error))
    }).finally(() => {
      setListLoaded(true);
    })
  }, []);

  // -- SAVE -- // 
  const onInvalid = () => {
    toast.error('Corrija os campos destacados antes de salvar.');
  };

  const onSubmit: SubmitHandler<CreateDistritoDto> = async (data) => {
    try {
      setIsExecuting(true);

      if (id) {
        await updateDistrito(data);
      }
      else {
        await createDistrito(data);
      }

      toast.info(`Distrito ${data.nome} salvo com sucesso.`)
    } catch (error) {
      toast.error(String(error))
    }
    finally {
      setIsExecuting(false);
    }
  };

  // -- DELETE -- //
  const handleExcluir = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    setModalExcluirShow(false);
    deleteDistrito(id)
      .then((res) => {
        toast.success(String(res));
        navigate(`${Links.Distrito_Index}`);
      })
      .catch((res) => {
        toast.error(String(res as string));
      });
  };

  if (!listsLoaded)
    return <Carregando />

  return (
    <>
      <div className="row">
        <div className="col-12">
          <h3>Distritos</h3>
        </div>
      </div>
      <hr className="pb-4" />

      <form onSubmit={(event) => { void handleSubmit(onSubmit, onInvalid)(event); }}>
        <div className="row">
          <div className="mb-3 col-md-2">
            <label htmlFor="ordem" className="form-label">
              Ordem:{' '}
              <span className="text-danger">*</span>
            </label>
            <input
              autoFocus={true}
              title="Ordem"
              type="number"
              className={formControlClass(!!errors.ordem)}
              min={0}
              {...register('ordem', { setValueAs: numberInputValueAs })}
            />
            <FieldError message={errors.ordem?.message} />
          </div>

          <div className="mb-3 col-md-10">
            <label htmlFor="nome" className="form-label">
              Nome:{' '}
              <span className="text-danger">*</span>
            </label>
            <input
              title="Nome"
              type="text"
              className={formControlClass(!!errors.nome)}
              {...register('nome')}
            />
            <FieldError message={errors.nome?.message} />
          </div>

          <div className="mb-3 col-md-10">
            <label htmlFor="defensoria" className="form-label">
              Defensoria:{' '}
            </label>
            <Controller
              name="defensoria"
              control={control}
              render={({ field }) => (
                <>
                  <Select
                    options={defensorias.map((d) => ({
                      value: d.id,
                      label: d.nome,
                    }))}
                    value={
                      field.value
                        ? {
                            value: field.value.id,
                            label: defensorias.find((d) => d.id === field.value!.id)?.nome || '',
                          }
                        : null
                    }
                    onChange={(option) =>
                      field.onChange(option ? { id: option.value, nome: option.label } : undefined)
                    }
                    placeholder="Selecione uma defensoria"
                    classNamePrefix="react-select"
                    className={errors.defensoria ? 'is-invalid' : undefined}
                  />
                  <FieldError
                    message={errors.defensoria?.id?.message ?? errors.defensoria?.message}
                  />
                </>
              )}
            />
          </div>

          <EditadoPorRodape dados={getValues()} />
          <div>
            <BotoesSalvarExcluir
              isLoading={isExecuting}
              texto={btnSalvar}
              textoLoading={btnSalvar === 'Cadastrar' ? ' Cadastrando...' : ' Salvando...'}
              id={id}
              onExcluirClick={() => setModalExcluirShow(true)}
            />
          </div>
        </div>
      </form>

      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <ModalExcluir
        show={modaExcluirShow}
        handleExcluir={handleExcluir}
        handleHide={() => setModalExcluirShow(false)}
      ></ModalExcluir>
      <ModalHistorico
        show={modalHistorico}
        handleHide={() => setModalHistorico(false)}
        tabela={Tabelas.distrito}
        idRegistro={id}
      ></ModalHistorico>
    </>
  );
}